import { AlertTriangle, Inbox, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { errorMessage } from "@/lib/api";

const TONES = {
  ok: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-900",
  warn: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-900",
  danger: "bg-red-50 text-red-800 border-red-200 dark:bg-red-950/50 dark:text-red-200 dark:border-red-900",
  info: "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-900",
  muted: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

export const StatusBadge = ({ map, value, testId }) => {
  const s = map[value] || { label: value, tone: "muted" };
  return (
    <span data-testid={testId} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${TONES[s.tone]}`}>
      {s.label}
    </span>
  );
};

export const PageHeader = ({ eyebrow, title, description, actions }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 sist-rise">
    <div>
      {eyebrow && <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#E11D2E] mb-2">{eyebrow}</p>}
      <h1 className="font-brand text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="page-title">{title}</h1>
      {description && <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-2xl">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const LoadingState = ({ rows = 4 }) => (
  <div className="space-y-3" data-testid="loading-state" aria-label="Memuat data" role="status">
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
  </div>
);

export const Spinner = () => <Loader2 className="h-4 w-4 animate-spin" aria-label="Memproses" />;

export const EmptyState = ({ title, description, action }) => (
  <div className="rounded-xl border border-dashed p-10 text-center" data-testid="empty-state">
    <Inbox className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
    <p className="mt-3 font-semibold">{title}</p>
    {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const ErrorState = ({ error, onRetry }) => {
  const code = error?.response?.status;
  const denied = code === 403 || code === 404;
  return (
  <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 p-6 flex items-start gap-3" data-testid="error-state" role="alert">
    <AlertTriangle className="h-5 w-5 text-[#E11D2E] shrink-0 mt-0.5" aria-hidden />
    <div className="flex-1">
      <p className="font-semibold text-red-900 dark:text-red-100">{denied ? "Akses ditolak" : "Gagal memuat data"}</p>
      <p className="text-sm text-red-800 dark:text-red-200 mt-1">{errorMessage(error)}</p>
    </div>
    {onRetry && !denied && (
      <Button variant="outline" size="sm" onClick={onRetry} data-testid="error-retry-button">
        <RotateCcw className="h-4 w-4 mr-1" /> Coba lagi
      </Button>
    )}
  </div>
  );
};

export const Notice = ({ tone = "info", children, testId }) => (
  <div className={`rounded-lg border px-4 py-3 text-sm ${TONES[tone]}`} data-testid={testId} role="status">{children}</div>
);
